import {
  Button,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import React, { type Dispatch, type FC } from "react";
import type { SortEntry } from "../../hooks/sort";

const sortOptions: Record<
  string,
  {
    label: string;
    directions: {
      asc: string;
      desc: string;
    };
  }
> = {
  color: {
    label: "Color",
    directions: {
      asc: "Ordered",
      desc: "Reversed",
    },
  },
  deltaE: {
    label: "ΔE",
    directions: {
      asc: "Ascending",
      desc: "Descending",
    },
  },
  grade: {
    label: "Grade",
    directions: {
      asc: "A...Z",
      desc: "Z...A",
    },
  },
  status: {
    label: "Color Status",
    directions: {
      asc: "A...Z",
      desc: "Z...A",
    },
  },
  finalColorCode: {
    label: "Color Code",
    directions: {
      asc: "0...9",
      desc: "9...0",
    },
  },
};

export interface SearchSortProps {
  value?: Array<SortEntry>;
  onChange?: Dispatch<Array<SortEntry>>;
  query: string;
}

export const SearchSort: FC<SearchSortProps> = ({ value, onChange, query }) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  function handleChange(
    property: string | undefined,
    direction: "asc" | "desc",
  ) {
    onChange?.(
      property
        ? [
            {
              property,
              direction,
            },
          ]
        : [],
    );
    handleClose();
  }

  const directions: Array<"asc" | "desc"> = ["asc", "desc"];
  const prop = value?.[0]?.property
    ? sortOptions[value?.[0]?.property]
    : undefined;
  const label = prop
    ? `${prop.label} ${prop.directions[value?.[0]?.direction!]}`
    : "Select Sort Order";

  return (
    <>
      {prop && (
        <Typography
          color="text.secondary"
          fontWeight={500}
          fontSize="var(--body-b2-14-regular-size, 14px)"
          lineHeight={"24px"}
        >
          Sorted by
        </Typography>
      )}
      <Button disabled={Boolean(query)} onClick={handleClick}>
        {label}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        MenuListProps={{
          sx: {
            minWidth: { xs: 200, sm: 240 },
            maxWidth: "calc(100vw - 32px)",
          },
        }}
      >
        <ListSubheader>Sort search results by</ListSubheader>
        <MenuItem onClick={() => handleChange(undefined, "asc")}>
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            alignItems={"center"}
            width={"100%"}
          >
            <Typography>-</Typography>
          </Stack>
        </MenuItem>
        {Object.entries(sortOptions).map(([property, option], _index) =>
          directions.map((direction) => (
            <MenuItem
              key={option.label + direction}
              onClick={() => handleChange(property, direction)}
            >
              <Stack
                direction={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
                width={"100%"}
              >
                <Typography>{option.label}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {option.directions[direction]}
                </Typography>
              </Stack>
            </MenuItem>
          )),
        )}
      </Menu>
    </>
  );
};
